import { z } from "zod";

import type { GetOrderResponse } from "~/server/domain/documents/docgen/pdf/bill-of-lading";

import { Nullable } from "~/shared/types/Nullable";
import { logger } from "~/utils/logger";

export const DocumentShippableEntitySchema = z.object({
  addressLines: z.string().array(),
  logoUrl: z.string().optional(),
  name: z.string(),
  phone: z.string().optional(),
  contact: z.string().optional(),
});
export type DocumentShippableEntity = z.infer<typeof DocumentShippableEntitySchema>;

export const DocumentAddressSchema = z.object({
  name: z.string().nullish(),
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  country: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  /** Issuance-only freeform postal lines; when supplied, these replace the structured postal fields. */
  addressLines: z.array(z.string()).optional(),
  contact: z.string().optional(),
});
export type DocumentAddress = z.infer<typeof DocumentAddressSchema>;

export const formatLocality = (city: Nullable<string>, state: Nullable<string>, zip: Nullable<string>): string => {
  const c = city?.trim();
  const s = state?.trim();
  const z = zip?.trim();
  const stateZip = [s, z].filter(Boolean).join(" ");
  return [c, stateZip].filter(Boolean).join(c && stateZip ? ", " : "");
};

export const formatMultilineAddressBlock = (name: string, addressLines: string[]): string =>
  [name.trim(), ...addressLines.map(line => line.trim()).filter(Boolean)].filter(Boolean).join("\n");

/** Append person/location contact name and phone below a name + address block (document prefill/PDF text fields). */
export const appendContactPhoneToAddressBlock = (block: string, contact?: string | null, phone?: string | null): string => {
  const lines = block.trim() ? [block.trim()] : [];
  const contactTrimmed = contact?.trim();
  if (contactTrimmed) {
    lines.push(contactTrimmed);
  }
  const phoneTrimmed = phone?.trim();
  if (phoneTrimmed) {
    lines.push(`Phone: ${phoneTrimmed}`);
  }
  return lines.join("\n");
};

export const formatDocumentShippableBlock = (entity: DocumentShippableEntity): string =>
  appendContactPhoneToAddressBlock(formatMultilineAddressBlock(entity.name, entity.addressLines), entity.contact, entity.phone);

type NamedPostalEntity = {
  name?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  contactName?: string | null;
  phone?: string | null;
};

export const toDocumentShippableEntity = (entity: NamedPostalEntity): DocumentShippableEntity => ({
  name: entity.name?.trim() ?? "",
  addressLines: [
    entity.street1?.trim() ?? "",
    entity.street2?.trim() ?? "",
    formatLocality(entity.city ?? "", entity.state ?? "", entity.zip ?? ""),
    entity.country?.trim() ?? "",
  ].filter(Boolean),
  contact: entity.contactName?.trim() ?? "",
  phone: entity.phone?.trim() ?? "",
});

export const formatNamedPostalEntityBlock = (entity: NamedPostalEntity): string =>
  formatDocumentShippableBlock(toDocumentShippableEntity(entity));

/** Keep manually entered postal lines intact; never infer city, region or contacts from free text. */
export const documentAddressToShippableEntity = (address: DocumentAddress): DocumentShippableEntity => ({
  ...toDocumentShippableEntity({ ...address, contactName: address.contact }),
  name: address.name ?? "",
  contact: address.contact ?? "",
  phone: address.phone ?? "",
  ...(address.addressLines === undefined ? {} : { addressLines: address.addressLines }),
});

const parseShipFromWarehouse = (order: ShipFromOrder): DocumentShippableEntity => {
  const orderWarehouse = getWarehouseForOrder(order);
  return {
    name: orderWarehouse?.name ?? "",
    addressLines: [
      orderWarehouse?.street1?.trim() ?? "",
      orderWarehouse?.street2?.trim() ?? "",
      formatLocality(orderWarehouse?.city ?? "", orderWarehouse?.state ?? "", orderWarehouse?.zip ?? ""),
      orderWarehouse?.country?.trim() ?? "",
    ].filter(Boolean),
    phone: orderWarehouse?.phone?.trim() ?? "",
    contact: orderWarehouse?.contactName?.trim() ?? "",
  };
};

type OrderPostalAddress = Pick<
  NonNullable<GetOrderResponse>["shipToAddress"],
  "name" | "street1" | "street2" | "city" | "state" | "zip" | "country" | "phone"
>;
type OrderItemLotWarehouse = OrderPostalAddress & Pick<NonNullable<NonNullable<GetOrderResponse>["warehouse"]>, "id" | "contactName">;

/**
 * What the ship-from derivation actually reads (SC-3786).
 *
 * These three functions declared the legacy bill of lading's ENTIRE order payload — items with
 * their products and packaging, the assigned user, the ordering contact — while reading four
 * branches of it. That is a signature demanding far more than it uses, and it meant a caller
 * loading only what ship-from needs could not satisfy it without an assertion.
 *
 * Narrowing a parameter is non-breaking: every existing caller passes a wider object and still
 * satisfies it structurally. The pieces are taken FROM `GetOrderResponse` rather than rewritten,
 * so a relation's shape stays whatever the query says it is.
 *
 * `items` is here because `getWarehouseForOrder` falls back to the lots' warehouses when the order
 * has none of its own — the one non-obvious dependency in the whole switch.
 */
export type ShipFromOrder = Pick<NonNullable<GetOrderResponse>, "id" | "type"> & {
  warehouse: OrderItemLotWarehouse | null;
  purchaseOrder: {
    supplier: OrderPostalAddress;
    shipFromLocation: OrderPostalAddress | null;
    supplierDelivery: boolean | null;
  } | null;
  items: { lot: { warehouse: OrderItemLotWarehouse | null } | null }[];
};

/** SO BOL ship-from: blind ship must not expose supplier identity (see getOrderShipFromLocation). */
export const parseShipFromForSalesOrderBol = (order: ShipFromOrder): DocumentShippableEntity => {
  if (order.type === "BLIND_SHIP") {
    return parseShipFromWarehouse(order);
  }
  return parseShipFrom(order);
};

export const parseShipFrom = (order: ShipFromOrder): DocumentShippableEntity => {
  const output: DocumentShippableEntity = {
    addressLines: [],
    name: "",
  };
  switch (order.type) {
    case "STOCK_ORDER":
      return parseShipFromWarehouse(order);
    case "DIRECT_SHIP": {
      const purchaseOrder = order.purchaseOrder;
      const shipFrom = purchaseOrder?.shipFromLocation;
      if (purchaseOrder?.supplierDelivery === true && !shipFrom) {
        return output;
      }
      output.name = order.purchaseOrder?.supplier.name ?? "";
      output.addressLines = [
        purchaseOrder?.shipFromLocation?.name?.trim() ?? "",
        shipFrom?.street1?.trim() ?? "",
        shipFrom?.street2?.trim() ?? "",
        formatLocality(shipFrom?.city ?? "", shipFrom?.state ?? "", shipFrom?.zip ?? ""),
        shipFrom?.country?.trim() ?? "",
      ].filter(Boolean);
      output.phone = shipFrom?.phone?.trim() ?? "";
      break;
    }
    case "BLIND_SHIP":
      output.name = order.purchaseOrder?.supplier.name ?? "";
      output.addressLines = [
        order.purchaseOrder?.supplier.street1?.trim() ?? "",
        order.purchaseOrder?.supplier.street2?.trim() ?? "",
        formatLocality(
          order.purchaseOrder?.supplier.city ?? "",
          order.purchaseOrder?.supplier.state ?? "",
          order.purchaseOrder?.supplier.zip ?? "",
        ),
        order.purchaseOrder?.supplier.country?.trim() ?? "",
      ].filter(Boolean);
      output.phone = order.purchaseOrder?.supplier.phone?.trim() ?? "";
      break;
  }

  return output;
};

/**
 * A party block an operator typed, normalised for printing (SC-3844).
 *
 * Lifted out of `docgen/pdf/bill-of-lading.ts`, where it was private, because the authored
 * template now honours the same manual ship-from and ship-to the legacy document does — and two
 * engines trimming a typed address differently would print two different consignors from one
 * modal. The trimming is not cosmetic: a blank address line left in the array prints as an empty
 * row in the party block.
 *
 * Nullish in, complete out: every field defaulted, so a caller never has to decide what an absent
 * contact means.
 */
export const formatShippableEntity = (shippableEntity: DocumentShippableEntity | null | undefined): DocumentShippableEntity => ({
  name: shippableEntity?.name ?? "",
  addressLines: shippableEntity?.addressLines.map(line => line.trim()).filter(Boolean) ?? [],
  phone: shippableEntity?.phone ?? "",
  contact: shippableEntity?.contact?.trim() ?? "",
});

export const parseShipTo = (shipToAddress: OrderPostalAddress): DocumentShippableEntity => ({
  name: shipToAddress.name,
  addressLines: [
    shipToAddress.street1?.trim() ?? "",
    shipToAddress.street2?.trim() ?? "",
    formatLocality(shipToAddress.city, shipToAddress.state, shipToAddress.zip),
    shipToAddress.country?.trim() ?? "",
  ].filter(Boolean),
  phone: shipToAddress.phone?.trim() ?? "",
});

const getWarehouseForOrder = (order: ShipFromOrder): Nullable<OrderItemLotWarehouse> => {
  if (!!order.warehouse) {
    return order.warehouse;
  }

  const itemWarehouses = order.items.reduce<Record<string, OrderItemLotWarehouse>>((acc, item) => {
    const warehouse = item.lot?.warehouse;
    if (!!warehouse) {
      if (!acc[warehouse.id]) {
        acc[warehouse.id] = warehouse;
      }
    }
    return acc;
  }, {});

  const warehouses = Object.values(itemWarehouses);
  if (!warehouses.length) {
    const errorMessage = "No Warehouse found to generate Bill of Lading";
    const supportMessage = "Please select Lots for the Sales Order or select a Warehouse on the Sales Order.";
    logger.error({
      message: errorMessage,
      supportMessage,
      orderId: order.id,
    });
    throw new Error(errorMessage);
  }
  if (warehouses.length > 1) {
    const errorMessage = "Too many Warehouses found for Bill of Lading";
    const supportMessage = "ShelfCycle currently supports a Sales Order having one Warehouse";
    logger.error({
      message: errorMessage,
      supportMessage,
      orderId: order.id,
    });
    throw new Error(errorMessage);
  }

  return warehouses?.[0] ?? null;
};
